import React, { useState } from 'react'
import {
  HiUserPlus,
  HiPencilSquare,
  HiArrowUpTray,
  HiChatBubbleBottomCenterText,
  HiExclamationTriangle,
  HiEyeSlash,
  HiClock,
  HiChevronDown,
  HiChevronRight,
  HiArrowsPointingOut,
  HiPaperClip,
  HiSparkles,
} from 'react-icons/hi2'
import { UserAvatar } from '../UserAvatar'
import { CommentViewModal } from '../../views/demand-view/components/modals/CommentViewModal'

export interface HistoryItem {
  id: string
  type:
    | 'created'
    | 'assigned'
    | 'status_changed'
    | 'priority_changed'
    | 'commented'
    | 'updated'
    | 'watched'
    | 'attachment_added'
    | 'attachment_removed'
  user: {
    id: number
    name: string
    email: string
    funcao_usuario: string
    cargo_usuario: string
    avatar?: string
  }
  timestamp: string
  description: string
  details?: {
    from?: string
    to?: string
    comment?: string
    attachment_name?: string
    affected_name?: string
    affected_email?: string
  }
  // Para cards expansíveis
  expandable?: boolean
  expanded?: boolean
  changes?: Array<{
    field: string
    fieldLabel: string
    from: string | Record<string, unknown>
    to: string | Record<string, unknown>
  }>
}

interface HistoryTimelineProps {
  items: HistoryItem[]
  className?: string
}

export const HistoryTimeline: React.FC<HistoryTimelineProps> = ({ items, className = '' }) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [commentModal, setCommentModal] = useState<{
    isOpen: boolean
    comment: string
    user: HistoryItem['user']
    timestamp: string
    action: string
  }>({
    isOpen: false,
    comment: '',
    user: {
      id: 0,
      name: '',
      email: '',
      funcao_usuario: '',
      cargo_usuario: '',
    },
    timestamp: '',
    action: '',
  })

  const toggleExpanded = (itemId: string) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId)
    } else {
      newExpanded.add(itemId)
    }
    setExpandedItems(newExpanded)
  }

  const openCommentModal = (item: HistoryItem) => {
    setCommentModal({
      isOpen: true,
      comment: item.details?.comment || '',
      user: item.user,
      timestamp: item.timestamp,
      action: getActionText(item),
    })
  }

  const closeCommentModal = () => {
    setCommentModal(prev => ({ ...prev, isOpen: false }))
  }

  // Função para fazer parsing dos dados de anexo do JSON
  const parseAttachmentData = (comment: string) => {
    try {
      const data = JSON.parse(comment)
      if (data.anexos && Array.isArray(data.anexos)) {
        return {
          attachments: data.anexos.map((anexo: any) => ({
            id: anexo.id,
            name: anexo.nome_anexo,
            path: anexo.anexo,
            comment: anexo.comentario_individual || '',
          })),
          comment: data.comentario || data.comentario_geral || '',
          hasIndividualComments: data.tem_comentarios_individuais || false,
        }
      }
    } catch {
      // Se não conseguir fazer parse, retorna o comentário original
    }
    return null
  }

  // Função auxiliar para formatar arrays de pagamentos
  const formatPagamentos = (pagamentos: any[]): string => {
    if (!Array.isArray(pagamentos) || pagamentos.length === 0) return 'Nenhum pagamento'
    
    return pagamentos.map((pag, idx) => {
      const nota = pag.nota || 'Sem nota'
      const data = pag.data_pagamento 
        ? new Date(pag.data_pagamento).toLocaleDateString('pt-BR') 
        : 'Sem data'
      return `Pagamento ${idx + 1}: ${nota.length > 50 ? nota.substring(0, 50) + '...' : nota} (${data})`
    }).join(' | ')
  }

  // Função para formatar valores de mudança (para campos como template_respostas)
  const formatChangeValue = (value: string | Record<string, unknown>, fieldName: string): string => {
    // Se o campo é template_respostas, tentar fazer parse e extrair informações úteis
    if (fieldName === 'template_respostas' || fieldName === 'Dados do Formulário') {
      try {
        // O valor pode ser um objeto ou uma string JSON
        const parsed = typeof value === 'string' ? JSON.parse(value) : value
        if (parsed && typeof parsed === 'object') {
          // Se tem respostas, listar os campos alterados
          if ((parsed as Record<string, unknown>).respostas && typeof (parsed as Record<string, unknown>).respostas === 'object') {
            const respostas = (parsed as Record<string, unknown>).respostas as Record<string, unknown>
            const campos = Object.entries(respostas)
              .filter(([_, v]) => v !== null && v !== undefined && v !== '')
              .map(([key, val]) => {
                const label = key.charAt(0).toUpperCase() + key.slice(1)
                
                // Tratamento especial para arrays de pagamentos
                if (key === 'pagamentos' && Array.isArray(val)) {
                  return `${label}: ${formatPagamentos(val)}`
                }
                // Outros arrays
                else if (Array.isArray(val)) {
                  if (val.length === 0) {
                    return `${label}: Nenhum item`
                  } else {
                    return `${label}: ${val.length} item(ns)`
                  }
                }
                // HTML/Richtext - remover tags e limitar tamanho
                else if (typeof val === 'string' && (val.includes('<p>') || val.includes('<br'))) {
                  const stripped = val.replace(/<[^>]*>/g, '').trim()
                  if (stripped) {
                    return `${label}: ${stripped.length > 50 ? stripped.substring(0, 50) + '...' : stripped}`
                  }
                  return null
                }
                // Strings normais
                else if (typeof val === 'string' && val) {
                  const displayVal = val.length > 50 ? val.substring(0, 50) + '...' : val
                  return `${label}: ${displayVal}`
                }
                // Outros valores
                else if (val !== null && val !== undefined && val !== '') {
                  return `${label}: ${String(val)}`
                }
                return null
              })
              .filter(Boolean)
            
            if (campos.length > 0) {
              return campos.slice(0, 3).join(' | ') + (campos.length > 3 ? ` (+${campos.length - 3} campos)` : '')
            }
          }
          // Fallback: mostrar template_id se disponível
          if ((parsed as Record<string, unknown>).template_id) {
            return `Template: ${(parsed as Record<string, unknown>).template_id}`
          }
        }
      } catch {
        // Se não conseguir fazer parse, continuar para o fallback
      }
    }
    
    // Se é um objeto (não string), tentar extrair informação útil
    if (typeof value === 'object' && value !== null) {
      return '(dados do formulário)'
    }
    
    // Verificar se é um objeto stringificado
    if (value === '[object Object]') {
      return '(dados do formulário)'
    }
    
    // Tentar fazer parse de JSON genérico
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value)
        if (typeof parsed === 'object' && parsed !== null) {
          return '(dados atualizados)'
        }
      } catch {
        // Não é JSON, retornar valor original
      }
    }
    
    return String(value)
  }

  // Função para obter label amigável para campos
  const getFieldLabel = (fieldName: string, originalLabel: string): string => {
    const labelMap: Record<string, string> = {
      'template_respostas': 'Dados do Formulário',
      'descricao': 'Descrição',
      'sugestao_entrega': 'Data de Entrega Sugerida',
      'prioridade': 'Prioridade',
      'status': 'Status',
    }
    return labelMap[fieldName] || originalLabel
  }

  // Função para renderizar HTML seguro dos comentários
  const renderCommentHTML = (comment: string) => {
    // Lista de tags HTML permitidas para comentários
    const allowedTags = [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      'ul',
      'ol',
      'li',
      'blockquote',
      'code',
      'pre',
    ]

    // Função para sanitizar HTML mantendo apenas tags seguras
    const sanitizeHTML = (html: string): string => {
      // Remove scripts e outros elementos perigosos
      let sanitized = html
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
        .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
        .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')
        .replace(/<link\b[^<]*>/gi, '')
        .replace(/<meta\b[^<]*>/gi, '')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')

      // Remove atributos perigosos de todas as tags
      sanitized = sanitized.replace(/<(\w+)[^>]*>/g, (_, tagName) => {
        if (allowedTags.includes(tagName.toLowerCase())) {
          return `<${tagName}>`
        }
        return ''
      })

      return sanitized
    }

    return { __html: sanitizeHTML(comment) }
  }
  const getActionIcon = (type: HistoryItem['type']) => {
    switch (type) {
      case 'created':
        return <HiSparkles className='w-4 h-4' />
      case 'assigned':
        return <HiUserPlus className='w-4 h-4' />
      case 'status_changed':
        return <HiArrowUpTray className='w-4 h-4' />
      case 'priority_changed':
        return <HiExclamationTriangle className='w-4 h-4' />
      case 'commented':
        return <HiChatBubbleBottomCenterText className='w-4 h-4' />
      case 'updated':
        return <HiPencilSquare className='w-4 h-4' />
      case 'watched':
        return <HiEyeSlash className='w-4 h-4' />
      case 'attachment_added':
        return <HiPaperClip className='w-4 h-4' />
      case 'attachment_removed':
        return <HiPaperClip className='w-4 h-4' />
      default:
        return <HiClock className='w-4 h-4' />
    }
  }

  const getActionColor = (type: HistoryItem['type']) => {
    switch (type) {
      case 'created':
        return 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400'
      case 'assigned':
        return 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
      case 'status_changed':
        return 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
      case 'priority_changed':
        return 'bg-amber-100 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400'
      case 'commented':
        return 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/20 dark:text-cyan-400'
      case 'updated':
        return 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400'
      case 'watched':
        return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
      case 'attachment_added':
        return 'bg-violet-100 text-violet-600 dark:bg-violet-900/20 dark:text-violet-400'
      case 'attachment_removed':
        return 'bg-rose-100 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400'
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
      return `${diffInMinutes} min atrás`
    } else if (diffInHours < 24) {
      return `${diffInHours}h atrás`
    } else if (diffInHours < 48) {
      return 'ontem'
    } else {
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    }
  }

  const getActionText = (item: HistoryItem) => {
    // Verificar se é um anexo com dados em JSON
    if (
      (item.type === 'attachment_added' || item.type === 'attachment_removed') &&
      item.details?.comment
    ) {
      const attachmentData = parseAttachmentData(item.details.comment)
      if (attachmentData && attachmentData.attachments.length > 0) {
        const count = attachmentData.attachments.length
        const hasComment = attachmentData.comment && attachmentData.comment.trim()
        const verb = item.type === 'attachment_removed' ? 'removeu' : 'adicionou'
        if (count === 1) {
          return hasComment ? `${verb} 1 anexo com comentário` : `${verb} 1 anexo`
        } else {
          return hasComment ? `${verb} ${count} anexos com comentário` : `${verb} ${count} anexos`
        }
      }
    }

    // Verificar descrição específica do backend primeiro
    const descLower = item.description.toLowerCase()
    
    // Ações específicas baseadas na descrição exata do backend
    // 1. Autorizou a demanda
    if (descLower.includes('autorizou a demanda')) {
      return item.details?.comment ? 'autorizou a demanda com comentário' : 'autorizou a demanda'
    }
    
    // 2. Reprovou a demanda
    if (descLower.includes('reprovou a demanda')) {
      return item.details?.comment ? 'reprovou a demanda com comentário' : 'reprovou a demanda'
    }
    
    // 3. Editou a demanda
    if (descLower.includes('editou a demanda')) {
      return item.details?.comment ? 'editou a demanda com comentário' : 'editou a demanda'
    }
    
    // 4. Atribuiu a demanda para si
    if (descLower.includes('atribuiu a demanda para si')) {
      return item.details?.comment ? 'atribuiu a demanda para si com comentário' : 'atribuiu a demanda para si'
    }
    
    // 5. Solucionou sua parte da demanda
    if (descLower.includes('solucionou sua parte da demanda')) {
      return item.details?.comment ? 'solucionou sua parte da demanda com comentário' : 'solucionou sua parte da demanda'
    }
    
    // 6. Solucionou a demanda
    if (descLower.includes('solucionou a demanda')) {
      return item.details?.comment ? 'solucionou a demanda com comentário' : 'solucionou a demanda'
    }
    
    // 7. Finalizou a demanda
    if (descLower.includes('finalizou a demanda')) {
      return item.details?.comment ? 'finalizou a demanda com comentário' : 'finalizou a demanda'
    }
    
    // 8. Devolveu a demanda
    if (descLower.includes('devolveu a demanda')) {
      return item.details?.comment ? 'devolveu a demanda com comentário' : 'devolveu a demanda'
    }
    
    // 9. Demanda enviada errada
    if (descLower.includes('demanda enviada errada')) {
      return item.details?.comment ? 'marcou a demanda como enviada errada com comentário' : 'marcou a demanda como enviada errada'
    }
    
    // 10. Reenviou a demanda
    if (descLower.includes('reenviou a demanda')) {
      // Se é expansível, mostrar contagem de alterações
      if (item.expandable && item.changes && item.changes.length > 0) {
        const alteracoes = item.changes.length
        const suffix = item.details?.comment ? ' com comentário' : ''
        return `reenviou a demanda (${alteracoes} alteraç${alteracoes === 1 ? 'ão' : 'ões'})${suffix}`
      }
      return item.details?.comment ? 'reenviou a demanda com comentário' : 'reenviou a demanda'
    }
    
    // 11. Desfez a solução da demanda
    if (descLower.includes('desfez a solução')) {
      return item.details?.comment ? 'desfez a solução da demanda com comentário' : 'desfez a solução da demanda'
    }
    
    // 12. Recusou a demanda
    if (descLower.includes('recusou a demanda')) {
      return item.details?.comment ? 'recusou a demanda com comentário' : 'recusou a demanda'
    }
    
    // 13. Aceitou a demanda
    if (descLower.includes('aceitou a demanda')) {
      return item.details?.comment ? 'aceitou a demanda com comentário' : 'aceitou a demanda'
    }
    
    // 14. Atribuiu a demanda para usuários
    if (descLower.includes('atribuiu a demanda para usuários') || descLower.includes('atribuiu a demanda para usuário')) {
      return item.details?.comment ? 'atribuiu a demanda para usuários com comentário' : 'atribuiu a demanda para usuários'
    }
    
    // 15. Removeu um atribuido da demanda
    if (descLower.includes('removeu um atribuido') || descLower.includes('removeu um atribuído')) {
      return item.details?.comment ? 'removeu um atribuído da demanda com comentário' : 'removeu um atribuído da demanda'
    }
    
    // 16. Fez um comentário
    if (descLower.includes('fez um comentário')) {
      return 'fez um comentário'
    }
    
    // 17 e 18. Adicionou anexos (anexar e anexarComComentarios)
    if (descLower.includes('adicionou anexos')) {
      return item.details?.comment ? 'adicionou anexos com comentário' : 'adicionou anexos'
    }
    
    // 19. Removeu anexos
    if (descLower.includes('removeu anexos')) {
      return item.details?.comment ? 'removeu anexos com comentário' : 'removeu anexos'
    }
    
    // 20. Adicionou observadores
    if (
      (descLower.includes('observador') || descLower.includes('observadores')) &&
      (descLower.includes('adicion') || descLower.includes('ativ'))
    ) {
      const alvo = item?.details?.affected_name;
      if (alvo) {
        return item.details?.comment
          ? `adicionou ${alvo} como observador com comentário`
          : `adicionou ${alvo} como observador`;
      }
      return item.details?.comment
        ? 'adicionou observadores com comentário'
        : 'adicionou observadores';
    }
    
    // 21. Removeu observador
    if (
      (descLower.includes('observador') || descLower.includes('observadores')) &&
      (descLower.includes('remov') || descLower.includes('exclu') || descLower.includes('desativ'))
    ) {
      const alvo = item?.details?.affected_name;
      if (alvo) {
        return item.details?.comment
          ? `removeu ${alvo} como observador com comentário`
          : `removeu ${alvo} como observador`;
      }
      return item.details?.comment
        ? 'removeu observador com comentário'
        : 'removeu observador';
    }
    
    // 22. Recusou ser um observador
    if (descLower.includes('recusou ser um observador') || descLower.includes('recusou ser observador')) {
      return item.details?.comment ? 'recusou ser um observador com comentário' : 'recusou ser um observador'
    }
    
    // 23. Excluiu a demanda
    if (descLower.includes('excluiu a demanda')) {
      return item.details?.comment ? 'excluiu a demanda com comentário' : 'excluiu a demanda'
    }

    // Fallback para tipos genéricos
    switch (item.type) {
      case 'created':
        // Para criação, verificar se tem comentário real (não JSON de anexos vazios)
        if (item.details?.comment) {
          const attachmentData = parseAttachmentData(item.details.comment)
          if (attachmentData) {
            // Se é JSON de anexos, verificar se tem comentário real
            if (attachmentData.comment && attachmentData.comment.trim()) {
              return 'criou esta demanda com comentário inicial'
            } else {
              return 'criou esta demanda'
            }
          } else {
            // Se não é JSON, é comentário normal
            return 'criou esta demanda com comentário inicial'
          }
        }
        return 'criou esta demanda'
      case 'assigned':
        return item.details?.comment
          ? 'assumiu a responsabilidade pela demanda com comentário'
          : 'assumiu a responsabilidade pela demanda'
      case 'status_changed':
        // Status normal (mudanças de status genéricas)
        if (item.details?.from && item.details?.to) {
          const statusText = `mudou o status de "${item.details.from}" para "${item.details.to}"`
          return item.details?.comment ? `${statusText} com comentário` : statusText
        }
        return item.details?.comment ? `mudou o status com comentário` : 'mudou o status'
      case 'priority_changed':
        { const priorityText = `alterou a prioridade de "${item.details?.from}" para "${item.details?.to}"`
        return item.details?.comment ? `${priorityText} com comentário` : priorityText }
      case 'commented':
        return 'fez um comentário'
      case 'updated':
        // Se tem detalhes from/to, é uma mudança detalhada de campo
        if (item.details?.from && item.details?.to) {
          return item.details?.comment ? `${item.description} com comentário` : item.description
        }
        return item.details?.comment
          ? 'fez alterações na demanda com comentário'
          : 'fez alterações na demanda'
      case 'watched':
        return item.details?.comment
          ? 'passou a acompanhar esta demanda com comentário'
          : 'passou a acompanhar esta demanda'
      case 'attachment_added':
        { const attachmentText = `anexou o arquivo "${item.details?.attachment_name}"`
        return item.details?.comment ? `${attachmentText} com comentário` : attachmentText }
      case 'attachment_removed':
        { const removedText = `removeu o arquivo "${item.details?.attachment_name}"`
        return item.details?.comment ? `${removedText} com comentário` : removedText }
      default:
        return item.details?.comment ? `${item.description} com comentário` : item.description
    }
  }

  if (items.length === 0) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <div className='text-center'>
          <HiClock className='w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600' />
          <p className='text-sm text-gray-500 dark:text-gray-400'>Nenhuma atividade registrada</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`relative w-full max-w-4xl mx-auto ${className}`}>
      {/* Linha principal da timeline */}
      <div className='absolute left-6 top-6 bottom-6 w-0.5 bg-gradient-to-b from-blue-200 via-gray-200 to-gray-100 dark:from-blue-700 dark:via-gray-600 dark:to-gray-700'></div>

      <div className='space-y-5'>
        {items.map(item => (
          <div key={item.id} className='relative'>
            {/* Card da atividade */}
            <div className='relative flex items-start gap-4 group'>
              {/* Ícone da ação com linha conectora */}
              <div className='relative flex-shrink-0'>
                <div
                  className={`relative z-20 w-12 h-12 rounded-full flex items-center justify-center shadow-sm border-2 border-white dark:border-gray-900 ${getActionColor(item.type)}`}
                >
                  {getActionIcon(item.type)}
                </div>
              </div>

              {/* Conteúdo do card */}
              <div className='flex-1 min-w-0 w-full max-w-3xl bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-200 group-hover:border-blue-200 dark:group-hover:border-blue-700'>
                {/* Cabeçalho */}
                <div className='p-4'>
                  <div className='flex items-start justify-between'>
                    <div className='flex items-start gap-3 flex-1 min-w-0'>
                      <UserAvatar user={item.user} size='sm' showName={false} showPopup={true} />
                      <div className='flex-1 min-w-0'>
                        <div className='flex flex-col gap-1.5'>
                          <div className='flex flex-wrap items-center gap-2'>
                            <span className='font-semibold text-gray-900 dark:text-gray-100 text-sm'>
                              {item.user.name}
                            </span>
                              <span className='text-gray-500 dark:text-gray-400 text-sm'>
                                {getActionText(item)}
                              </span>
                          </div>
                          <div className='flex items-center gap-2 text-xs'>
                            <span className='text-gray-500 dark:text-gray-400 font-medium'>
                              {formatTimestamp(item.timestamp)}
                            </span>
                            <span className='text-gray-300 dark:text-gray-600'>•</span>
                            <span className='text-gray-400 dark:text-gray-500 capitalize'>
                              {item.user.funcao_usuario}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Botão de expandir para items com changes ou comentários */}
                    {(() => {
                      // Verificar se tem mudanças
                      const hasChanges = item.expandable && item.changes && item.changes.length > 0

                      // Verificar se tem comentário real
                      let hasRealComment = false
                      if (item.details?.comment) {
                        if (
                          item.type === 'attachment_added' ||
                          item.type === 'attachment_removed' ||
                          item.type === 'created'
                        ) {
                          const attachmentData = parseAttachmentData(item.details.comment)
                          if (attachmentData) {
                            // Tem anexos ou comentário real
                            hasRealComment =
                              attachmentData.attachments.length > 0 ||
                              (attachmentData.comment && attachmentData.comment.trim())
                          } else {
                            // Não é JSON, então é comentário normal
                            hasRealComment = true
                          }
                        } else {
                          // Para outros tipos, considerar que tem comentário
                          hasRealComment = true
                        }
                      }

                      return (
                        (hasChanges || hasRealComment) && (
                          <button
                            onClick={() => toggleExpanded(item.id)}
                            className='flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 ml-2 flex-shrink-0'
                          >
                            {expandedItems.has(item.id) ? (
                              <HiChevronDown className='w-4 h-4 text-gray-500 dark:text-gray-400' />
                            ) : (
                              <HiChevronRight className='w-4 h-4 text-gray-500 dark:text-gray-400' />
                            )}
                          </button>
                        )
                      )
                    })()}
                  </div>
                </div>

                {/* Conteúdo expansível - mudanças e comentários */}
                {expandedItems.has(item.id) && (
                  <div className='border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50'>
                    <div className='p-4 space-y-4'>
                      {/* Lista de anexos para attachment_added */}
                      {item.details?.comment &&
                        item.type === 'attachment_added' &&
                        (() => {
                          const attachmentData = parseAttachmentData(item.details.comment)
                          if (attachmentData && attachmentData.attachments.length > 0) {
                            return (
                              <div className='bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-4 border-l-4 border-purple-300 dark:border-purple-500'>
                                <div className='flex items-start space-x-2'>
                                  <HiPaperClip className='w-4 h-4 text-purple-500 dark:text-purple-400 mt-0.5 flex-shrink-0' />
                                  <div className='flex-1'>
                                    <p className='text-sm font-medium text-purple-800 dark:text-purple-200 mb-3'>
                                      {attachmentData.attachments.length === 1
                                        ? 'Anexo adicionado:'
                                        : 'Anexos adicionados:'}
                                    </p>
                                    <div className='space-y-3'>
                                      {attachmentData.attachments.map(
                                        (
                                          attachment: {
                                            id: number
                                            name: string
                                            path: string
                                            comment: string
                                          },
                                          index: number
                                        ) => (
                                          <div
                                            key={index}
                                            className='border border-purple-200/50 dark:border-purple-700/50 rounded-lg p-3 bg-purple-50/50 dark:bg-purple-900/10'
                                          >
                                            <div className='flex items-start space-x-2 mb-2'>
                                              <div className='w-2 h-2 bg-purple-400 dark:bg-purple-300 rounded-full flex-shrink-0 mt-1.5'></div>
                                              <span className='text-sm font-medium text-gray-700 dark:text-gray-300 break-all'>
                                                {attachment.name}
                                              </span>
                                            </div>
                                            {/* Comentário individual do anexo */}
                                            {attachment.comment && attachment.comment.trim() && (
                                              <div className='ml-4 mt-2 pl-3 border-l-2 border-purple-300/50 dark:border-purple-600/50'>
                                                <p className='text-xs font-medium text-purple-700 dark:text-purple-300 mb-1'>
                                                  Comentário do arquivo:
                                                </p>
                                                <p className='text-xs text-gray-600 dark:text-gray-400 italic'>
                                                  {attachment.comment}
                                                </p>
                                              </div>
                                            )}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )
                          }
                          return null
                        })()}

                      {/* Lista de anexos para attachment_removed */}
                      {item.details?.comment &&
                        item.type === 'attachment_removed' &&
                        (() => {
                          const attachmentData = parseAttachmentData(item.details.comment)
                          if (attachmentData && attachmentData.attachments.length > 0) {
                            return (
                              <div className='bg-gradient-to-r from-rose-50 to-red-50 dark:from-rose-900/20 dark:to-red-900/20 rounded-lg p-4 border-l-4 border-rose-300 dark:border-rose-500'>
                                <div className='flex items-start space-x-2'>
                                  <HiPaperClip className='w-4 h-4 text-rose-500 dark:text-rose-400 mt-0.5 flex-shrink-0' />
                                  <div className='flex-1'>
                                    <p className='text-sm font-medium text-rose-800 dark:text-rose-200 mb-3'>
                                      {attachmentData.attachments.length === 1
                                        ? 'Anexo removido:'
                                        : 'Anexos removidos:'}
                                    </p>
                                    <div className='space-y-3'>
                                      {attachmentData.attachments.map(
                                        (
                                          attachment: {
                                            id: number
                                            name: string
                                            path: string
                                            comment: string
                                          },
                                          index: number
                                        ) => (
                                          <div
                                            key={index}
                                            className='border border-rose-200/50 dark:border-rose-700/50 rounded-lg p-3 bg-rose-50/50 dark:bg-rose-900/10'
                                          >
                                            <div className='flex items-start space-x-2 mb-2'>
                                              <div className='w-2 h-2 bg-rose-400 dark:bg-rose-300 rounded-full flex-shrink-0 mt-1.5'></div>
                                              <span className='text-sm font-medium text-gray-700 dark:text-gray-300 break-all'>
                                                {attachment.name}
                                              </span>
                                            </div>
                                            {/* Comentário individual do anexo */}
                                            {attachment.comment && attachment.comment.trim() && (
                                              <div className='ml-4 mt-2 pl-3 border-l-2 border-rose-300/50 dark:border-rose-600/50'>
                                                <p className='text-xs font-medium text-rose-700 dark:text-rose-300 mb-1'>
                                                  Motivo/Comentário:
                                                </p>
                                                <p className='text-xs text-gray-600 dark:text-gray-400 italic'>
                                                  {attachment.comment}
                                                </p>
                                              </div>
                                            )}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )
                          }
                          return null
                        })()}

                      {/* Comentários */}
                      {item.details?.comment &&
                        (() => {
                          // Para anexos e criações, extrair o comentário do JSON se existir
                          if (
                            item.type === 'attachment_added' ||
                            item.type === 'attachment_removed' ||
                            item.type === 'created'
                          ) {
                            const attachmentData = parseAttachmentData(item.details.comment)
                            if (
                              attachmentData &&
                              attachmentData.comment &&
                              attachmentData.comment.trim()
                            ) {
                              return (
                                <div className='bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border-l-4 border-blue-300 dark:border-blue-500'>
                                  <div className='flex items-start space-x-2'>
                                    <HiChatBubbleBottomCenterText className='w-4 h-4 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0' />
                                    <div className='flex-1'>
                                      <div className='flex items-center justify-between mb-2'>
                                        <p className='text-sm font-medium text-blue-800 dark:text-blue-200'>
                                          Comentário:
                                        </p>
                                        <button
                                          onClick={() =>
                                            openCommentModal({
                                              ...item,
                                              details: {
                                                ...item.details,
                                                comment: attachmentData.comment,
                                              },
                                            })
                                          }
                                          className='flex items-center space-x-1 px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors duration-200'
                                          title='Expandir comentário'
                                        >
                                          <HiArrowsPointingOut className='w-3 h-3' />
                                          <span>Expandir</span>
                                        </button>
                                      </div>
                                      <div
                                        className='text-sm text-gray-700 dark:text-gray-300 leading-relaxed prose prose-sm max-w-none dark:prose-invert break-words'
                                        style={{
                                          display: '-webkit-box',
                                          WebkitLineClamp: 3,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                          wordBreak: 'break-word',
                                          overflowWrap: 'break-word',
                                        }}
                                        dangerouslySetInnerHTML={renderCommentHTML(
                                          attachmentData.comment
                                        )}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )
                            } else if (!attachmentData) {
                              // Se não conseguiu fazer parse do JSON, é comentário normal
                              return (
                                <div className='bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border-l-4 border-blue-300 dark:border-blue-500'>
                                  <div className='flex items-start space-x-2'>
                                    <HiChatBubbleBottomCenterText className='w-4 h-4 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0' />
                                    <div className='flex-1'>
                                      <div className='flex items-center justify-between mb-2'>
                                        <p className='text-sm font-medium text-blue-800 dark:text-blue-200'>
                                          Comentário:
                                        </p>
                                        <button
                                          onClick={() => openCommentModal(item)}
                                          className='flex items-center space-x-1 px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors duration-200'
                                          title='Expandir comentário'
                                        >
                                          <HiArrowsPointingOut className='w-3 h-3' />
                                          <span>Expandir</span>
                                        </button>
                                      </div>
                                      <div
                                        className='text-sm text-gray-700 dark:text-gray-300 leading-relaxed prose prose-sm max-w-none dark:prose-invert break-words'
                                        style={{
                                          display: '-webkit-box',
                                          WebkitLineClamp: 3,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                          wordBreak: 'break-word',
                                          overflowWrap: 'break-word',
                                        }}
                                        dangerouslySetInnerHTML={renderCommentHTML(
                                          item.details.comment
                                        )}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )
                            }
                          } else {
                            // Para outros tipos, usar o comentário normal
                            return (
                              <div className='bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border-l-4 border-blue-300 dark:border-blue-500'>
                                <div className='flex items-start space-x-2'>
                                  <HiChatBubbleBottomCenterText className='w-4 h-4 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0' />
                                  <div className='flex-1'>
                                    <div className='flex items-center justify-between mb-2'>
                                      <p className='text-sm font-medium text-blue-800 dark:text-blue-200'>
                                        Comentário:
                                      </p>
                                      <button
                                        onClick={() => openCommentModal(item)}
                                        className='flex items-center space-x-1 px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors duration-200'
                                        title='Expandir comentário'
                                      >
                                        <HiArrowsPointingOut className='w-3 h-3' />
                                        <span>Expandir</span>
                                      </button>
                                    </div>
                                    <div
                                      className='text-sm text-gray-700 dark:text-gray-300 leading-relaxed prose prose-sm max-w-none dark:prose-invert break-words'
                                      style={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                      }}
                                      dangerouslySetInnerHTML={renderCommentHTML(
                                        item.details.comment
                                      )}
                                    />
                                  </div>
                                </div>
                              </div>
                            )
                          }
                          return null
                        })()}

                      {/* Mudanças detalhadas de campos */}
                      {item.expandable &&
                        item.changes &&
                        item.changes.map((change, index) => (
                          <div
                            key={index}
                            className='bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-lg p-4 border-l-4 border-orange-200 dark:border-orange-600'
                          >
                            <div className='mb-3'>
                              <span className='text-sm font-medium text-orange-800 dark:text-orange-200'>
                                {getFieldLabel(change.field, change.fieldLabel)}
                              </span>
                            </div>
                            <div className='flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 text-sm'>
                              <div className='flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2'>
                                <span className='text-gray-600 dark:text-gray-400 font-medium'>
                                  De:
                                </span>
                                <div className='bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 px-2 py-1 rounded text-xs font-mono border border-red-200 dark:border-red-700 max-w-full break-words'>
                                  {formatChangeValue(change.from, change.field)}
                                </div>
                              </div>
                              <div className='text-gray-400 hidden sm:block'>→</div>
                              <div className='flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2'>
                                <span className='text-gray-600 dark:text-gray-400 font-medium'>
                                  Para:
                                </span>
                                <div className='bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 px-2 py-1 rounded text-xs font-mono border border-green-200 dark:border-green-700 max-w-full break-words'>
                                  {formatChangeValue(change.to, change.field)}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Mudanças simples (fallback para itens sem changes) */}
                {item.type === 'updated' &&
                  item.details?.from &&
                  item.details?.to &&
                  !item.expandable && (
                    <div className='border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50'>
                      <div className='p-4'>
                        <div className='bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-lg p-4 border-l-4 border-orange-200 dark:border-orange-600'>
                          <div className='flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 text-sm'>
                            <div className='flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2'>
                              <span className='text-gray-600 dark:text-gray-400 font-medium'>
                                De:
                              </span>
                              <div className='bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 px-2 py-1 rounded text-xs font-mono border border-red-200 dark:border-red-700 max-w-full break-words'>
                                {item.details.from}
                              </div>
                            </div>
                            <div className='text-gray-400 hidden sm:block'>→</div>
                            <div className='flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2'>
                              <span className='text-gray-600 dark:text-gray-400 font-medium'>
                                Para:
                              </span>
                              <div className='bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 px-2 py-1 rounded text-xs font-mono border border-green-200 dark:border-green-700 max-w-full break-words'>
                                {item.details.to}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de visualização de comentário */}
      <CommentViewModal
        isOpen={commentModal.isOpen}
        onClose={closeCommentModal}
        comment={commentModal.comment}
        user={commentModal.user}
        timestamp={commentModal.timestamp}
        action={commentModal.action}
      />
    </div>
  )
}
